import React, { useState, useEffect, SyntheticEvent } from 'react'
import get from 'lodash/get'
import {
  IIntegration,
  IIntegrationApplicationField,
  IIntegrationApplication,
} from '@api/integration/types'
import { Tab, Tabs, FormGroup, Classes, Intent } from '@blueprintjs/core'
import { Button, IconButton, Confirmation, Dialog, Callout } from '@ui'
import IntegrationInputGroup from './IntegrationInputGroup'
import { reduceAppFields, capitalize, isValidEmail } from './utils'

const isFieldInvalid = (field: IIntegrationApplicationField) =>
  (field.required && !field.value) || (field.name === 'email' && !isValidEmail(field.value))

const isFormInvalid = (applications: IIntegrationApplication[], selectedTabId: string) => {
  const app = applications.find(({ name }) => name === selectedTabId)
  if (!app) {
    return false
  }
  const countOfEmptyFields = app.fields.reduce(
    (count: any, field) => (isFieldInvalid(field) ? count + 1 : count),
    0
  )
  return countOfEmptyFields > 0
}

export default function AvailableModal({
  isOpen,
  onClose,
  integration,
  enableIntegration,
}: {
  isOpen: boolean
  onClose: () => void
  integration: IIntegration
  enableIntegration: (source: string, id: string, fields: any) => Promise<IIntegration>
}) {
  const firstAppName = get(integration, 'applications[0].name')
  const [selectedTabId, setSelectedTabId] = useState(firstAppName)
  const [applications, setApplications] = useState(integration.applications)
  const [dirty, setDirty] = useState(false)
  const [errors, setErrors] = useState<any>({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setApplications(applications)
  }, [applications])

  const onCloseAndReset = () => {
    onClose()
    setDirty(false)
    setError('')
  }

  return (
    <Dialog
      style={{ width: 700 }}
      onClose={onCloseAndReset}
      isOpen={isOpen}
      usePortal
      className="bp4-dialog-override"
      title={integration.name}
      canEscapeKeyClose={!dirty}
      canOutsideClickClose={!dirty}
      isCloseButtonShown={false}
      transitionDuration={200}
      toolbar={
        <div className="dialog-buttons">
          {dirty ? (
            <Confirmation text="Discard unsaved changes?" onConfirm={onCloseAndReset}>
              <IconButton minimal tooltip="Close" iconName="times" />
            </Confirmation>
          ) : (
            <IconButton minimal tooltip="Close" iconName="times" onClick={onCloseAndReset} />
          )}
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <div
            style={{
              fontSize: 16,
              borderBottom: '1px solid var(--border-color)',
              padding: '0 0 20px 0',
            }}
          >
            {integration.description}
          </div>
          <Tabs selectedTabId={selectedTabId} onChange={(id: string) => setSelectedTabId(id)}>
            {applications.map((application) => (
              <Tab
                key={application.name}
                id={application.name}
                title={capitalize(application.name)}
                panel={
                  <>
                    {error && (
                      <Callout
                        style={{ marginBottom: 10 }}
                        faIcon="exclamation-triangle"
                        intent={Intent.DANGER}
                      >
                        {error}
                      </Callout>
                    )}
                    <ApplicationPanel
                      {...application}
                      errors={errors}
                      loading={loading}
                      setDirty={setDirty}
                      setErrors={setErrors}
                      setFields={(fields: IIntegrationApplicationField[]) => {
                        setApplications(
                          applications.map((a: IIntegrationApplication) =>
                            a.name === application.name
                              ? {
                                  ...application,
                                  fields,
                                }
                              : a
                          )
                        )
                      }}
                    />
                  </>
                }
              />
            ))}
          </Tabs>
        </div>
      }
      footer={
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            {dirty ? (
              <Confirmation text="Discard unsaved changes?" onConfirm={onCloseAndReset}>
                <Button style={{ marginRight: 8 }} type="button" text="Cancel" />
              </Confirmation>
            ) : (
              <Button style={{ marginRight: 8 }} text="Cancel" onClick={onCloseAndReset} />
            )}
            <Button
              loading={loading}
              disabled={isFormInvalid(applications, selectedTabId)}
              intent={Intent.PRIMARY}
              text="Activate"
              onClick={() => {
                const fields = reduceAppFields(applications, selectedTabId)
                setLoading(true)
                enableIntegration(integration.source, integration.id, fields)
                  .then(() => {
                    setLoading(false)
                    setDirty(false)
                    setApplications(integration.applications)
                    onClose()
                  })
                  .catch(() => {
                    setLoading(false)
                    setError('Unable to activate integration')
                  })
              }}
            />
          </div>
        </div>
      }
    />
  )
}

const ApplicationPanel = ({
  description,
  fields,
  errors,
  loading,
  setDirty,
  setErrors,
  setFields,
}: {
  description: string[]
  errors: any
  loading: boolean
  fields: IIntegrationApplicationField[]
  setDirty: (para: boolean) => void
  setFields: (fields: IIntegrationApplicationField[]) => void
  setErrors: (param: any) => void
}) => {
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        {description.map((d: string) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {fields.map((field: IIntegrationApplicationField) => (
        <FormGroup
          key={field.name}
          helperText={field.steps.map((s: string) => s)}
          label={field.description}
          labelFor={field.name}
          labelInfo={field.required ? '(required)' : field.readonly ? '(read-only)' : ''}
        >
          <IntegrationInputGroup
            {...field}
            generatoable={false}
            disabled={loading}
            hasError={errors[field.name]}
            onChange={({ currentTarget: { name, value } }: SyntheticEvent<HTMLInputElement>) => {
              setDirty(true)
              setErrors({
                ...errors,
                [field.name]: isFieldInvalid({ ...field, value }),
              })
              setFields(fields.map((f) => (f.name === field.name ? { ...f, value } : f)))
            }}
            onFocus={() => setErrors({ ...errors, [field.name]: false })}
            onBlur={() =>
              setErrors({
                ...errors,
                [field.name]: isFieldInvalid(field),
              })
            }
          />
        </FormGroup>
      ))}
    </div>
  )
}
